import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { VELLUM as T, SANS, MONO, mono } from "./kit";

/* The one dropdown in the project.

   A native <select> cannot be drawn. Its closed face takes CSS, but the list
   it opens is painted by the operating system - rounded, blue-highlighted,
   in a font nobody here chose - so a form that is ruled everywhere else
   still opens a foreign panel at every choice. This is that control rebuilt
   out of the sheet's own parts: a cell, a hairline schedule, an accent rule
   on the entry in hand.

   It is the ARIA 1.2 combobox-with-listbox pattern rather than a div with a
   click handler. Focus never leaves the trigger; `aria-activedescendant`
   names the active option, so a screen reader announces the list exactly as
   it announces a native one, and the keyboard behaves the way a select is
   expected to: type-ahead, Home/End, Alt+Down, Escape.

   WHAT IS GIVEN UP. A native select validates itself - `required` on an
   empty value blocks submit and the browser points at the field. That is
   gone, and no ARIA attribute brings it back: `required` here is announced,
   not enforced. Forms with an optional-looking placeholder row must check
   the value themselves before submitting. */

export interface FolioSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface FolioSelectProps {
  value: string;
  onChange: (value: string) => void;
  /* Plain strings are taken as their own label and value. */
  options: ReadonlyArray<FolioSelectOption | string>;
  /* Shown when the value matches no option - the unchosen state. */
  placeholder?: string;
  id?: string;
  name?: string;
  disabled?: boolean;
  /* Announced to assistive tech. NOT enforced - see the note above. */
  required?: boolean;
  /* Drop the control's own box; the caller draws the cell around it. */
  bare?: boolean;
  size?: "sm" | "md";
  /* Toolbar controls are set in mono uppercase like the rules around them;
     form fields are set in the sans the rest of a field cell uses. */
  tone?: "sans" | "mono";
  /* Which edge the list is hung from, for a trigger sitting at the right. */
  align?: "left" | "right";
  width?: number | string;
  ariaLabel?: string;
  labelledBy?: string;
  className?: string;
  style?: React.CSSProperties;
}

const SP = "fsel";

const normalise = (o: FolioSelectOption | string): FolioSelectOption =>
  typeof o === "string" ? { value: o, label: o } : o;

/* Emitted once for the whole app rather than per instance: twenty-four
   dropdowns must not mean twenty-four identical <style> tags. Only the rules
   that cannot be expressed inline live here - a pseudo-class cannot be a
   style object. */
let stylesInjected = false;
const injectStyles = () => {
  if (stylesInjected || typeof document === "undefined") return;
  stylesInjected = true;
  const el = document.createElement("style");
  el.dataset.folioSelect = "";
  el.textContent = `
    .${SP}-trigger:focus-visible { outline: 2px solid ${T.accent}; outline-offset: 2px; }
    .${SP}-boxed:focus-within { box-shadow: inset 0 0 0 1.5px ${T.accent}; }
    @media (prefers-reduced-motion: reduce) { .${SP}-caret { transition: none !important; } }
  `;
  document.head.appendChild(el);
};

export const FolioSelect: React.FC<FolioSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select…",
  id,
  name,
  disabled = false,
  required = false,
  bare = false,
  size = "md",
  tone = "sans",
  align = "left",
  width,
  ariaLabel,
  labelledBy,
  className,
  style,
}) => {
  const items = useMemo(() => options.map(normalise), [options]);
  const selectedIndex = items.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? items[selectedIndex] : undefined;

  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropUp, setDropUp] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const typeahead = useRef<{ buffer: string; at: number }>({ buffer: "", at: 0 });

  const baseId = id || name || "folio-select";
  const listboxId = `${baseId}-listbox`;
  const optionId = (i: number) => `${baseId}-opt-${i}`;

  useEffect(injectStyles, []);

  /* The first move into an open list lands on the current value, so a
     keyboard arrow steps from where the reader already is. */
  const openList = useCallback(() => {
    if (disabled) return;
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setOpen(true);
  }, [disabled, selectedIndex]);

  const closeList = useCallback((refocus = true) => {
    setOpen(false);
    setActiveIndex(-1);
    if (refocus) triggerRef.current?.focus();
  }, []);

  const commit = useCallback(
    (index: number) => {
      const opt = items[index];
      if (!opt || opt.disabled) return;
      onChange(opt.value);
      closeList();
    },
    [items, onChange, closeList],
  );

  /* Hung upward when the sheet has run out of room below, so a choice near
     the foot of the page is not opened off-screen. */
  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    setDropUp(below < 260 && r.top > below);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open || activeIndex < 0 || !listRef.current) return;
    listRef.current
      .querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  const step = useCallback(
    (from: number, dir: 1 | -1) => {
      if (items.length === 0) return -1;
      let i = from;
      for (let n = 0; n < items.length; n++) {
        i += dir;
        if (i < 0) i = items.length - 1;
        if (i >= items.length) i = 0;
        if (!items[i].disabled) return i;
      }
      return -1;
    },
    [items],
  );

  const edge = useCallback(
    (dir: 1 | -1) => {
      const order = dir === 1 ? items.map((_, i) => i) : items.map((_, i) => items.length - 1 - i);
      for (const i of order) if (!items[i].disabled) return i;
      return -1;
    },
    [items],
  );

  /* Type-ahead. Repeating one letter cycles the entries starting with it,
     which is what a native select does and what fingers expect. */
  const seek = useCallback(
    (char: string) => {
      const now = Date.now();
      const ta = typeahead.current;
      ta.buffer = now - ta.at > 700 ? char : ta.buffer + char;
      ta.at = now;

      const q = ta.buffer.toLowerCase();
      const repeated = q.length > 1 && q.split("").every((c) => c === q[0]);
      const needle = repeated ? q[0] : q;
      const from = open ? (activeIndex >= 0 ? activeIndex : selectedIndex) : selectedIndex;

      for (let n = 1; n <= items.length; n++) {
        const i = (Math.max(from, 0) + (repeated ? n : n - 1)) % items.length;
        const o = items[i];
        if (!o.disabled && o.label.toLowerCase().startsWith(needle)) {
          if (open) setActiveIndex(i);
          else onChange(o.value);
          return;
        }
      }
    },
    [items, open, activeIndex, selectedIndex, onChange],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openList();
        return;
      }
      /* Closed, a letter moves the value directly - again, as a native
         select does. */
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        seek(e.key);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (e.altKey) return;
        setActiveIndex((i) => step(i, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        if (e.altKey) {
          closeList();
          return;
        }
        setActiveIndex((i) => step(i, -1));
        break;
      case "Home":
        e.preventDefault();
        setActiveIndex(edge(1));
        break;
      case "End":
        e.preventDefault();
        setActiveIndex(edge(-1));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (activeIndex >= 0) commit(activeIndex);
        else closeList();
        break;
      case "Escape":
        e.preventDefault();
        closeList();
        break;
      case "Tab":
        /* Tab leaves the field: the list closes without choosing, and the
           browser is left to move focus on its own. */
        setOpen(false);
        setActiveIndex(-1);
        break;
      default:
        if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
          e.preventDefault();
          seek(e.key);
        }
    }
  };

  const pad = size === "sm" ? "9px 12px" : "12px 14px";
  const font = size === "sm" ? 14 : 15.5;

  /* Bare, the control sits in a cell that draws no padding of its own - the
     field styles carry it. So it must repeat fieldInput's 13px/14px exactly
     rather than going flush: a select set 14px left of every input beside it
     breaks the one vertical the eye actually reads down. */
  const barePad = size === "sm" ? "9px 12px" : "13px 14px";

  /* One face for both the trigger and its entries, so the list does not
     change voice the moment it opens. */
  const face: React.CSSProperties =
    tone === "mono"
      ? { fontFamily: MONO, fontSize: 11, fontWeight: 500, letterSpacing: "0.14em", textTransform: "uppercase" }
      : { fontFamily: SANS, fontSize: font };

  return (
    <div
      ref={rootRef}
      className={className}
      style={{ position: "relative", width: width ?? "100%", ...style }}
    >
      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-haspopup="listbox"
        aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
        aria-required={required || undefined}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        onClick={() => (open ? closeList() : openList())}
        onKeyDown={onKeyDown}
        className={`${SP}-trigger${bare ? "" : ` ${SP}-boxed`}`}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          width: "100%",
          textAlign: "left",
          border: bare ? "none" : `1.5px solid ${T.ink}`,
          borderRadius: 0,
          background: bare ? "transparent" : T.ground,
          padding: bare ? barePad : pad,
          ...face,
          color: selected ? T.ink : T.mute,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <svg
          aria-hidden
          className={`${SP}-caret`}
          width="9"
          height="6"
          viewBox="0 0 9 6"
          fill="none"
          style={{
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 150ms ease",
          }}
        >
          <path d="M1 1L4.5 4.5L8 1" stroke={T.mute} strokeWidth="1.3" strokeLinecap="square" />
        </svg>
      </button>

      {/* Rendered only while open: an aria-hidden listbox left in the tree is
          still a listbox to some assistive tech. */}
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel}
          aria-labelledby={labelledBy}
          tabIndex={-1}
          style={{
            position: "absolute",
            zIndex: 40,
            top: dropUp ? "auto" : "100%",
            bottom: dropUp ? "100%" : "auto",
            left: align === "right" ? "auto" : 0,
            right: align === "right" ? 0 : "auto",
            marginTop: dropUp ? 0 : 4,
            marginBottom: dropUp ? 4 : 0,
            minWidth: "100%",
            maxHeight: 264,
            overflowY: "auto",
            listStyle: "none",
            margin: 0,
            padding: 0,
            border: `1.5px solid ${T.ink}`,
            background: T.ground,
          }}
        >
          {items.map((o, i) => {
            const isActive = i === activeIndex;
            const isSelected = o.value === value;
            return (
              <li
                key={`${o.value}-${i}`}
                id={optionId(i)}
                role="option"
                aria-selected={isSelected}
                aria-disabled={o.disabled || undefined}
                data-index={i}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(i);
                }}
                onMouseEnter={() => !o.disabled && setActiveIndex(i)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: size === "sm" ? "8px 12px" : "9px 13px",
                  ...face,
                  lineHeight: 1.35,
                  color: o.disabled ? T.mute : T.ink,
                  fontWeight: isSelected ? 600 : 400,
                  background: isActive && !o.disabled ? "rgba(10,110,126,0.08)" : "transparent",
                  cursor: o.disabled ? "not-allowed" : "pointer",
                  borderTop: i === 0 ? "none" : `1px solid ${T.rule}`,
                  whiteSpace: "nowrap",
                }}
              >
                {/* The entry in hand is marked on the rule as well as by the
                    tint, because a tint alone is not visible to everyone. */}
                <span
                  aria-hidden
                  style={{
                    width: 2,
                    alignSelf: "stretch",
                    flexShrink: 0,
                    background: isActive || isSelected ? T.accent : "transparent",
                  }}
                />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
                {isSelected && (
                  <span aria-hidden style={{ ...mono(T, T.accent), marginLeft: "auto", fontSize: 9 }}>
                    ✓
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FolioSelect;
